import { blueprintBg } from "@/theme/styleHelpers";
/**
 * Instrument Rack — the persistent control plane sitting at the bottom of
 * `AppLayout`. Reads as a single 56px instrument-panel strip with four
 * segments (project context · active draft · EDA presence · credits) and
 * a blueprint crosshatch backdrop.
 *
 * Why this exists (UX/UI report §4): the previous layout surfaced active
 * project, active version, draft snapshot, plugin presence, and credit
 * balance in five different places — sidebar pills, header chips,
 * drawers, modals. Once §10 live-sync ships, that fragmentation breaks
 * because none of those spots can house a "Heinrich is editing in
 * pcbnew" indicator without a layout rewrite. The rack consolidates the
 * lot into a single always-readable strip, with each segment a drop-in
 * for future signals.
 *
 * Architectural rules:
 *   * The rack reads from existing stores (`projectStore`, `draftStore`)
 *     and existing hooks (`useWallet`); it owns no state.
 *   * Each segment is a stand-alone component with bounded width so the
 *     rack lays out predictably under the responsive sidebar.
 *   * Segments degrade gracefully — empty / loading / unconfigured each
 *     have a calm null state. No layout jumps when data arrives late.
 */
import { Box, Divider, Stack, useMediaQuery, useTheme } from "@mui/material";

import { CreditsSegment } from "./CreditsSegment";
import { DraftSegment } from "./DraftSegment";
import { EdaPresenceSegment } from "./EdaPresenceSegment";
import { NotificationsBell } from "./NotificationsBell";
import { ProjectSegment } from "./ProjectSegment";
import { VersionSegment } from "./VersionSegment";

export const INSTRUMENT_RACK_HEIGHT = 56;

interface InstrumentRackProps {
  /** When false the rack is rendered as a 0px shell — used by the login /
   *  landing routes where there's no application chrome. */
  visible?: boolean;
}

export function InstrumentRack({ visible = true }: InstrumentRackProps) {
  const theme = useTheme();
  // Hide the EDA-presence placeholder below md so the rack stays
  // readable on tablets / phones — the four-segment row collides
  // around 720px otherwise.
  const showEda = useMediaQuery(theme.breakpoints.up("md"));
  // The build stamp is informational; drop it below sm so the three
  // must-have segments (project / draft / credits) own the phone width.
  const showVersion = useMediaQuery(theme.breakpoints.up("sm"));
  if (!visible) return null;
  return (
    <Box
      component="footer"
      aria-label="Instrument rack — workspace status"
      sx={(t) => ({
        ...blueprintBg(t),
        height: INSTRUMENT_RACK_HEIGHT,
        flex: "0 0 auto",
        borderTop: `1px solid ${t.palette.border.default}`,
        // The rack is the brand surface — no shadow, just hairline + grid.
        position: "relative",
        zIndex: t.zIndex.appBar - 1,
      })}
    >
      {/* Responsive segment visibility:
            * xs (<600px): Project + Draft + Credits — the three the user
              actually needs at a glance. EDA presence collapses (it's a
              soon-state placeholder anyway).
            * sm+ (600px+): all four segments + the divider trail.
          The horizontal scrollbar fallback (overflowX:auto) catches the
          edge case where the active project's name is unusually long
          on a phone. */}
      <Stack
        direction="row"
        alignItems="stretch"
        sx={{
          height: "100%",
          px: 0,
          gap: 0,
          overflowX: { xs: "auto", sm: "visible" },
          // Hide horizontal scrollbar on mobile — the segments are
          // semi-truncating already (eyebrow + value); no need for a
          // visible scrollbar that eats vertical space.
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "border.default" }}
          />
        }
      >
        <ProjectSegment />
        <DraftSegment />
        {showEda && <EdaPresenceSegment />}
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <CreditsSegment />
        <NotificationsBell />
        {showVersion && <VersionSegment />}
      </Stack>
    </Box>
  );
}
